/**
 * Platform detection. Used to apply mobile-only behaviour tweaks
 * (e.g. dice energy-gain boost) without affecting touch laptops.
 *
 * Returns true ONLY for actual Android / iOS devices (native Capacitor
 * shell OR a mobile browser running our PWA over LAN). Touch-capable
 * Windows laptops, Surfaces, Chromebooks etc. return false.
 */

import { Capacitor } from '@capacitor/core';

let cachedIsMobile: boolean | null = null;

/**
 * True only inside the native iOS Capacitor shell. Used to gate the native
 * Google sign-in path, which is broken on the iOS binary until the next build
 * ships the iOS OAuth client + REVERSED_CLIENT_ID URL scheme. Android-native
 * (fixed via the upload-cert SHA-1 in Firebase) and web Safari (working GIS
 * flow) are intentionally NOT covered here.
 */
export function isIOSNative(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

/** True only inside the native Android Capacitor shell (system WebView = Chromium). */
export function isAndroidNative(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

/**
 * Tag <html> with the native platform so CSS can branch on it — chiefly to
 * disable backdrop-filter on Android, where blurring the live WebGL canvas
 * under an animating overlay flickers (Chromium WebView; WKWebView is fine).
 * Safe to call once at boot.
 */
export function applyPlatformClasses(): void {
  if (!Capacitor.isNativePlatform()) return;
  const p = Capacitor.getPlatform();
  if (p === 'android') document.documentElement.classList.add('platform-android');
  else if (p === 'ios') document.documentElement.classList.add('platform-ios');
}

export function isMobilePlatform(): boolean {
  if (cachedIsMobile !== null) return cachedIsMobile;
  // Capacitor native build is the definitive case.
  if (Capacitor.isNativePlatform()) {
    const p = Capacitor.getPlatform();
    cachedIsMobile = p === 'ios' || p === 'android';
    return cachedIsMobile;
  }
  // Browser case — match the UA. Covers testing the dev build on the
  // phone over LAN (the use case that exposed the dice-energy gap).
  // iPadOS 13+ reports as Mac in UA; include the maxTouchPoints check
  // to catch it. Touch-laptops report platform='Win32' and pass this.
  const ua = navigator.userAgent;
  const isAndroidOrIOS = /Android|iPhone|iPod/i.test(ua);
  const isIpadOS =
    /Macintosh/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
  cachedIsMobile = isAndroidOrIOS || isIpadOS;
  return cachedIsMobile;
}
